// src/pages/Notificaciones.js
import React, { useState, useEffect, useCallback } from "react";
import { FaShieldAlt } from "react-icons/fa";
import {
  Box, Typography, Chip, Divider, CircularProgress,
  Checkbox, Tooltip, IconButton
} from "@mui/material";
import {
  FaBell, FaCheck, FaCheckDouble, FaTrash, FaFilter,
  FaFileAlt, FaRoute, FaChartLine, FaBullhorn, FaUsers,
  FaEye, FaEyeSlash
} from "react-icons/fa";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://vol-backend.onrender.com";

// ── Configuración por tipo ─────────────────────────────────────────
const TYPE_CONFIG = {
  doc_nuevo:             { color: "#3b82f6", bg: "#eff6ff", label: "Documentos",  cat: "documentos" },
  doc_validado:          { color: "#22c55e", bg: "#f0fdf4", label: "Documentos",  cat: "documentos" },
  doc_rechazado:         { color: "#ef4444", bg: "#fef2f2", label: "Documentos",  cat: "documentos" },
  trayectoria_nueva:     { color: "#f59e0b", bg: "#fffbeb", label: "Trayectorias",cat: "trayectorias" },
  trayectoria_validada:  { color: "#22c55e", bg: "#f0fdf4", label: "Trayectorias",cat: "trayectorias" },
  trayectoria_rechazada: { color: "#ef4444", bg: "#fef2f2", label: "Trayectorias",cat: "trayectorias" },
  entrevista_aprobada:   { color: "#3b82f6", bg: "#eff6ff", label: "Entrevistas", cat: "entrevistas" },
  solicitud_entrevista:  { color: "#3b82f6", bg: "#eff6ff", label: "Entrevistas", cat: "entrevistas" },
  aviso_institucional:   { color: "#f59e0b", bg: "#fffbeb", label: "Avisos",      cat: "avisos" },
  constancia_emitida:    { color: "#22c55e", bg: "#f0fdf4", label: "Actividades", cat: "actividades" },
  uploads_pendientes:    { color: "#f59e0b", bg: "#fffbeb", label: "Progresos",   cat: "progresos" },
  docs_pendientes:       { color: "#f59e0b", bg: "#fffbeb", label: "Progresos",   cat: "progresos" },
  backlog_nacional:      { color: "#ef4444", bg: "#fef2f2", label: "Nacional",    cat: "nacional" },
  programa_completado:   { color: "#8b5cf6", bg: "#f5f3ff", label: "Actividades", cat: "actividades" },
  solicitud_usuario:     { color: "#7c3aed", bg: "#f5f3ff", label: "Solicitudes", cat: "solicitudes" },
};
  
// Categorías por rol
const CATS_ASPIRANTE = [
  { key: "all",          label: "Todas",        icon: <FaBell /> },
  { key: "documentos",   label: "Documentos",   icon: <FaFileAlt /> },
  { key: "trayectorias", label: "Trayectorias", icon: <FaRoute /> },
  { key: "actividades",  label: "Actividades",  icon: <FaChartLine /> },
  { key: "avisos",       label: "Avisos del administrador", icon: <FaBullhorn /> },
];

const CATS_MODERADOR = [
  { key: "all",          label: "Todas",                    icon: <FaBell /> },
  { key: "documentos",   label: "Documentos",               icon: <FaFileAlt /> },
  { key: "trayectorias", label: "Trayectorias",             icon: <FaRoute /> },
  { key: "progresos",    label: "Progresos",                icon: <FaChartLine /> },
  { key: "avisos",       label: "Avisos del administrador", icon: <FaBullhorn /> },
  { key: "entrevistas",  label: "Solicitudes / Entrevistas",icon: <FaUsers /> },
];

const CATS_ADMIN = [
  { key: "all",           label: "Todas",                   icon: <FaBell /> },
  { key: "documentos",    label: "Documentos",              icon: <FaFileAlt /> },
  { key: "trayectorias",  label: "Trayectorias",            icon: <FaRoute /> },
  { key: "progresos",     label: "Progresos",               icon: <FaChartLine /> },
  { key: "entrevistas",   label: "Solicitudes de Entrevista", icon: <FaUsers /> },
  { key: "solicitudes",  label: "Solicitudes de Moderadores", icon: <FaShieldAlt /> }, 
  { key: "control",       label: "Control de Moderadores",  icon: <FaShieldAlt /> },
];

// Links rápidos por tipo
const QUICK_LINKS = {
  doc_nuevo:             { label: "Revisar documentos", path: "/Documentos" },
  doc_validado:          { label: "Ver documentos",    path: "/MiPerfil" },
  doc_rechazado:         { label: "Ver documentos",    path: "/MiPerfil" },
  trayectoria_nueva:     { label: "Revisar trayectoria", path: "/Trayectorias" },
  trayectoria_validada:  { label: "Ver trayectoria",    path: "/Trayectoria" },
  trayectoria_rechazada: { label: "Ver trayectoria",    path: "/Trayectoria" },
  entrevista_aprobada:   { label: "Ver inscripciones", path: "/Plataforma" },
  solicitud_entrevista:  { label: "Ver solicitudes",   path: "/Usuarios" },
  uploads_pendientes:    { label: "Ir a Progresos",    path: "/Progresos" },
  docs_pendientes:       { label: "Ir a Progresos",  path: "/Progresos"  },
  aviso_institucional:   { label: "Ver avisos",        path: "/Plataforma" },
  solicitud_usuario: { label: "Ver solicitudes", path: "/Notificaciones" },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "Ahora";
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Ayer";
  if (d < 7)  return `Hace ${d} días`;
  return new Date(dateStr).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

async function apiFetch(path, options = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-firebase-uid": user.uid,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) return null;
  return res.json();
}

export default function Notificaciones({ userRole = "aspirante" }) {
  const navigate = useNavigate();
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [catActiva, setCatActiva]     = useState("all");
  const [filtro, setFiltro]           = useState("all"); // "all" | "unread" | "read"
  const [selected, setSelected]       = useState([]);    // ids seleccionados
  const [unreadCount, setUnreadCount] = useState(0);
  const [readCount, setReadCount]     = useState(0);

  const [tabActiva, setTabActiva]         = useState("bandeja"); // "bandeja" | "dashboard"
  const [dashboard, setDashboard]         = useState(null);
  const [loadingDash, setLoadingDash]     = useState(false);

  // ── Estados para control de moderadores ─────────────────
  const [controlData, setControlData]         = useState(null);
  const [loadingControl, setLoadingControl]   = useState(false);
  const [estadoSeleccionado, setEstadoSel]    = useState(null); // nombre del estado
  const [modSeleccionado, setModSel]          = useState(null); // { uid, nombre }
  const [notifMod, setNotifMod]              = useState(null); // notificaciones del mod
  const [loadingNotifMod, setLoadingNotifMod] = useState(false);
  const [filtroMod, setFiltroMod]            = useState("all"); // "all" | "unread"

  // ── PASO 3: Estados para resúmenes por categoría ──────────────────
  const [resumenDocs, setResumenDocs]           = useState(null);
  const [resumenTray, setResumenTray]           = useState(null);
  const [resumenProg, setResumenProg]           = useState(null);
  const [resumenEntrev, setResumenEntrev]       = useState(null);
  const [resumenSolicitudes, setResumenSolicitudes] = useState(null);
  const [loadingResumen, setLoadingResumen]     = useState(false);
  const [accionando, setAccionando]             = useState(null); // solicitud_id en proceso
  const [enviandoAviso, setEnviandoAviso]       = useState(null); // estado al que se envía

  const isAdmin = userRole === "admin";
  const isStaff = userRole === "admin" || userRole === "moderador";
  
  const cats = isAdmin ? CATS_ADMIN : (isStaff ? CATS_MODERADOR : CATS_ASPIRANTE);
  

  // ── Fetch ──────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const data = await apiFetch("/api/notifications?limit=100");
    if (data) {
      setItems(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      setReadCount((data.notifications || []).filter(n => n.is_read).length);
    }
    setLoading(false);
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingDash(true);

    // Esperar a que Firebase tenga el currentUser listo
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();

    // Retry hasta 3 veces si currentUser no está listo
    let attempts = 0;
    while (!auth.currentUser && attempts < 10) {
      await new Promise(r => setTimeout(r, 300));
      attempts++;
    }

    const data = await apiFetch("/api/notifications/dashboard-nacional");
    if (data) setDashboard(data);
    setLoadingDash(false);
  }, [isAdmin]);

  // ── Fetch del control de moderadores ────────────────────
  const fetchControl = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingControl(true);
    const data = await apiFetch("/api/notifications/control-moderadores");
    if (data) setControlData(data);
    setLoadingControl(false);
  }, [isAdmin]);

  const fetchNotifMod = useCallback(async (modUid, soloSinLeer = false) => {
    setLoadingNotifMod(true);
    const data = await apiFetch(
      `/api/notifications/control-moderadores/${modUid}?limit=50&sin_leer=${soloSinLeer}`
    );
    if (data) setNotifMod(data);
    setLoadingNotifMod(false);
  }, []);

  // ── PASO 3: Fetch de resúmenes por categoría ──────────────────────
  const fetchResumen = useCallback(async (tipo) => {
    if (!isAdmin) return;
    setLoadingResumen(true);
    const data = await apiFetch(`/api/notifications/resumen-${tipo}`);
    if (tipo === "documentos")   setResumenDocs(data);
    if (tipo === "trayectorias") setResumenTray(data);
    if (tipo === "progresos")    setResumenProg(data);
    if (tipo === "entrevistas")  setResumenEntrev(data);
    setLoadingResumen(false);
  }, [isAdmin]);

  const fetchResumenSolicitudes = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingResumen(true);
    const data = await apiFetch("/users/solicitudes");
    if (data) setResumenSolicitudes(data.solicitudes || []);
    setLoadingResumen(false);
  }, [isAdmin]);

  const resolverSolicitud = async (solicitudId, accion) => {
  setAccionando(solicitudId);
  try {
    const data = await apiFetch(`/users/solicitudes/${solicitudId}/resolver`, {
      method: "PATCH",
      body: JSON.stringify({ accion }),
    });
    if (data?.ok) {
      setResumenSolicitudes(prev => (prev || []).filter(s => s.solicitud_id !== solicitudId));
      // marcar notificaciones relacionadas como leídas en el estado local
      setItems(prev => prev.map(n => {
        if (n.type !== "solicitud_usuario") return n;
        try {
          const meta = typeof n.metadata === "string" ? JSON.parse(n.metadata) : (n.metadata || {});
          if (Number(meta?.solicitud_id) === Number(solicitudId)) return { ...n, is_read: 1 };
        } catch (_) {}
        return n;
      }));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  } finally {
    setAccionando(null);
  }
  };

  // ── PASO 3: Enviar aviso a moderador ──────────────────────────────
  const enviarAvisoModerador = async (estado) => {
    setEnviandoAviso(estado);
    const data = await apiFetch("/api/notifications/avisar-moderador", {
      method: "POST",
      body: JSON.stringify({ estado }),
    });
    setEnviandoAviso(null);
    if (data?.ok) alert(`✅ Aviso enviado a ${data.moderadores_notificados} moderador(es) de ${estado}`);
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (isAdmin && tabActiva === "dashboard") fetchDashboard();
  }, [isAdmin, tabActiva, fetchDashboard]);

  // ── Efecto para cargar control cuando se selecciona la categoría ──
  useEffect(() => {
    if (isAdmin && catActiva === "control") fetchControl();
  }, [isAdmin, catActiva, fetchControl]);

  // ── PASO 3: Efecto para cargar resúmenes al cambiar categoría ─────
  useEffect(() => {
    if (!isAdmin) return;
    const map = {
      documentos: "documentos",
      trayectorias: "trayectorias",
      progresos: "progresos",
      entrevistas: "entrevistas",
    };
    if (map[catActiva]) fetchResumen(map[catActiva]);
    if (catActiva === "solicitudes") fetchResumenSolicitudes();
  }, [isAdmin, catActiva, fetchResumen, fetchResumenSolicitudes]);

  // ── Filtrado local ─────────────────────────────────────────────────
  const itemsFiltrados = items.filter(n => {
    const cfg = TYPE_CONFIG[n.type];
    const catOk = catActiva === "all" || cfg?.cat === catActiva;
    const readOk = filtro === "all"
      ? true
      : filtro === "unread" ? !n.is_read : n.is_read;
    return catOk && readOk;
  });

  // ── Conteos por categoría ──────────────────────────────────────────
  const countByCat = (catKey) => {
    if (catKey === "all") return unreadCount;
    return items.filter(n => {
      const cfg = TYPE_CONFIG[n.type];
      return cfg?.cat === catKey && !n.is_read;
    }).length;
  };

  // ── Acciones ───────────────────────────────────────────────────────
  const markRead = async (id) => {
    await apiFetch(`/api/notifications/${id}/read`, { method: "PATCH" });
    setItems(prev => prev.map(n =>
      n.notification_id === id ? { ...n, is_read: 1 } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await apiFetch("/api/notifications/read-all", { method: "PATCH" });
    setItems(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setUnreadCount(0);
  };

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    await apiFetch("/api/notifications/bulk", {
      method: "DELETE",
      body: JSON.stringify({ ids: selected }),
    });
    setItems(prev => prev.filter(n => !selected.includes(n.notification_id)));
    setSelected([]);
  };

  const deleteAll = async () => {
    await apiFetch("/api/notifications/bulk", {
      method: "DELETE",
      body: JSON.stringify({ delete_all: true }),
    });
    setItems([]);
    setSelected([]);
    setUnreadCount(0);
  };

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === itemsFiltrados.length) {
      setSelected([]);
    } else {
      setSelected(itemsFiltrados.map(n => n.notification_id));
    }
  };

  // ── Aviso fijo (si hay avisos institucionales no leídos) ───────────
  const avisoFijo = items.find(n => n.type === "aviso_institucional" && !n.is_read);

  return (
    <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>

      {/* ══════════════════════════════════════════════════
          COLUMNA IZQUIERDA — categorías + acciones rápidas
      ══════════════════════════════════════════════════ */}
      <Box sx={{ width: 220, flexShrink: 0 }}>

        {/* Categorías */}
        <Box sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden", mb: 2,
        }}>
          <Box sx={{ backgroundColor: "#ff3333", px: 2, py: 1.2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 1 }}>
              CATEGORÍAS
            </Typography>
          </Box>
          {cats.map(cat => {
            const count = countByCat(cat.key);
            const activa = catActiva === cat.key;
            return (
              <Box
                key={cat.key}
                onClick={() => setCatActiva(cat.key)}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 2, py: 1.2, cursor: "pointer",
                  backgroundColor: activa ? "#fff8f8" : "#fff",
                  borderLeft: activa ? "3px solid #ff3333" : "3px solid transparent",
                  transition: "0.15s",
                  "&:hover": { backgroundColor: "#fff8f8" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ color: activa ? "#ff3333" : "#888", fontSize: "0.85rem" }}>
                    {cat.icon}
                  </Box>
                  <Typography sx={{
                    fontSize: "0.82rem",
                    color: activa ? "#ff3333" : "#444",
                    fontWeight: activa ? 700 : 400,
                  }}>
                    {cat.label}
                  </Typography>
                </Box>
                {count > 0 && (
                  <Chip
                    label={count}
                    size="small"
                    sx={{
                      backgroundColor: "#ff3333", color: "#fff",
                      height: 18, fontSize: "0.68rem",
                      "& .MuiChip-label": { px: 0.8 },
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Acciones rápidas */}
        <Box sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden", mb: 2,
        }}>
          <Box sx={{ backgroundColor: "#555", px: 2, py: 1.2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 1 }}>
              ACCIONES RÁPIDAS
            </Typography>
          </Box>
          {[
            { label: "Ver solo no leídas",  icon: <FaEye />,     action: () => setFiltro("unread") },
            { label: "Ver solo leídas",     icon: <FaEyeSlash />,action: () => setFiltro("read") },
            { label: "Limpiar filtros",     icon: <FaFilter />,  action: () => { setFiltro("all"); setCatActiva("all"); } },
          ].map((a, i) => (
            <Box
              key={i}
              onClick={a.action}
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                px: 2, py: 1.1, cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
                transition: "0.15s",
              }}
            >
              <Box sx={{ color: "#888", fontSize: "0.8rem" }}>{a.icon}</Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#555" }}>{a.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Borrar notificaciones */}
        <Box sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          p: 2,
        }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#444", mb: 1 }}>
            Borrar notificaciones
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#888", mb: 1.5, lineHeight: 1.4 }}>
            Puedes eliminar todas las notificaciones o seleccionar las que ya no necesites.
          </Typography>
          <Box
            onClick={deleteSelected}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
              py: 0.8, borderRadius: 1, cursor: "pointer",
              border: "1px solid #ef4444", color: "#ef4444",
              fontSize: "0.78rem", fontWeight: 600,
              "&:hover": { backgroundColor: "#fef2f2" },
              transition: "0.15s",
              opacity: selected.length === 0 ? 0.4 : 1,
            }}
          >
            <FaTrash size={11} />
            Eliminar seleccionadas ({selected.length})
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════
          COLUMNA CENTRAL — lista de notificaciones
      ══════════════════════════════════════════════════ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* Header */}
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FaBell size={22} color="#ff3333" />
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: "#222" }}>
                Notificaciones
              </Typography>
            </Box>
            {/* Fecha + marcar leídas para admin */}
            {isAdmin && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>
                  {new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}
                  {" · "}
                  {new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                </Typography>
                <Box onClick={markAllRead} sx={{
                  display: "flex", alignItems: "center", gap: 0.8,
                  cursor: "pointer", color: "#ff3333", fontSize: "0.8rem", fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}>
                  <FaCheckDouble size={12} />
                  Marcar todas como leídas
                </Box>
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: "0.83rem", color: "#888" }}>
            {isAdmin
              ? "Visibilidad y control total de la operación nacional."
              : "Mantente al día con tus avisos, actualizaciones y mensajes importantes."}
          </Typography>
        </Box>

        {/* Tabs Bandeja / Dashboard — solo admin */}
        {isAdmin && (
          <Box sx={{ display: "flex", mb: 3, borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0" }}>
            {[
              { key: "bandeja",    label: "Bandeja de notificaciones", sub: "Todas las notificaciones del sistema" },
              { key: "dashboard",  label: "Dashboard Nacional",         sub: "Resumen y control operativo del país", color: "#ff3333" },
            ].map(tab => (
              <Box
                key={tab.key}
                onClick={() => setTabActiva(tab.key)}
                sx={{
                  flex: 1, px: 3, py: 2, cursor: "pointer",
                  backgroundColor: tabActiva === tab.key ? (tab.color || "#fff") : "#f8f8f8",
                  borderRight: "1px solid #e0e0e0",
                  transition: "0.2s",
                  "&:last-child": { borderRight: "none" },
                }}
              >
                <Typography sx={{
                  fontWeight: 700, fontSize: "0.88rem",
                  color: tabActiva === tab.key && tab.color ? "#fff" : "#333",
                }}>
                  {tab.label}
                </Typography>
                <Typography sx={{
                  fontSize: "0.73rem",
                  color: tabActiva === tab.key && tab.color ? "rgba(255,255,255,0.8)" : "#888",
                  mt: 0.3,
                }}>
                  {tab.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── DASHBOARD NACIONAL (solo admin) ── */}
        {isAdmin && tabActiva === "dashboard" && (
          <Box>
            {loadingDash ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : dashboard ? (
              <>
                {/* Resumen Nacional */}
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
                  🛡️ RESUMEN NACIONAL — Estado actual de la operación en todo el país.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                  {[
                    { label: "Estados con backlog +7 días", value: dashboard.totales.estados_con_backlog, color: "#ef4444", bg: "#fef2f2", icon: "!" },
                    { label: "Documentos sin revisar",      value: dashboard.totales.docs_pendientes,     color: "#3b82f6", bg: "#eff6ff", icon: "📄" },
                    { label: "Trayectorias pendientes",     value: dashboard.totales.trayectorias,        color: "#f59e0b", bg: "#fffbeb", icon: "🎯" },
                    { label: "Aspirantes con backlog",      value: dashboard.totales.aspirantes_backlog,  color: "#8b5cf6", bg: "#f5f3ff", icon: "👥" },
                  ].map(card => (
                    <Box key={card.label} sx={{
                      flex: "1 1 180px", backgroundColor: card.bg,
                      borderRadius: 2, p: 2.5,
                      border: `1px solid ${card.color}22`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <Typography sx={{ fontSize: "0.75rem", color: "#666", mb: 0.5, lineHeight: 1.3 }}>
                        {card.label}
                      </Typography>
                      <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: card.color, lineHeight: 1 }}>
                        {card.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Tabla por estado */}
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1 }}>
                  👥 Detalle por estado — Estados con mayor carga de actividades pendientes.
                </Typography>
                <Box sx={{
                  backgroundColor: "#fff", borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden", mb: 3,
                }}>
                  {/* Header tabla */}
                  <Box sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    px: 2, py: 1.2,
                    backgroundColor: "#f8f8f8",
                    borderBottom: "1px solid #e0e0e0",
                  }}>
                    {["Estado", "Backlog +7 días", "Docs pendientes", "Trayectorias"].map(h => (
                      <Typography key={h} sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>
                        {h}
                      </Typography>
                    ))}
                  </Box>

                  {dashboard.por_estado.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                        ✅ Sin pendientes registrados actualmente
                      </Typography>
                    </Box>
                  ) : dashboard.por_estado.slice(0, 10).map((row, idx) => {
                    const total = row.backlog + row.docs + row.trayectorias;
                    const nivel = total > 20 ? "#ef4444" : total > 10 ? "#f59e0b" : "#22c55e";
                    return (
                      <Box key={row.estado} sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr",
                        px: 2, py: 1.4,
                        borderBottom: idx < Math.min(dashboard.por_estado.length, 10) - 1 ? "1px solid #f0f0f0" : "none",
                        "&:hover": { backgroundColor: "#fef2f2" },
                        transition: "0.15s",
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: nivel }} />
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
                            {row.estado}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.85rem", color: row.backlog > 0 ? "#ef4444" : "#888", fontWeight: row.backlog > 0 ? 700 : 400 }}>
                          {row.backlog || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: row.docs > 0 ? "#3b82f6" : "#888", fontWeight: row.docs > 0 ? 700 : 400 }}>
                          {row.docs || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: row.trayectorias > 0 ? "#f59e0b" : "#888", fontWeight: row.trayectorias > 0 ? 700 : 400 }}>
                          {row.trayectorias || "—"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ color: "#aaa" }}>No se pudo cargar el dashboard</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── CONTROL DE MODERADORES (solo admin, categoría control) ── */}
        {isAdmin && catActiva === "control" && (
          <Box>
            {loadingControl ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : controlData ? (
              <>
                {/* Breadcrumb */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Typography
                    onClick={() => { setEstadoSel(null); setModSel(null); setNotifMod(null); }}
                    sx={{ fontSize: "0.82rem", color: estadoSeleccionado ? "#ff3333" : "#333",
                      cursor: estadoSeleccionado ? "pointer" : "default", fontWeight: 700,
                      "&:hover": estadoSeleccionado ? { textDecoration: "underline" } : {} }}
                  >
                    Todos los estados
                  </Typography>
                  {estadoSeleccionado && (
                    <>
                      <Typography sx={{ color: "#aaa" }}>›</Typography>
                      <Typography
                        onClick={() => { setModSel(null); setNotifMod(null); }}
                        sx={{ fontSize: "0.82rem", color: modSeleccionado ? "#ff3333" : "#333",
                          cursor: modSeleccionado ? "pointer" : "default", fontWeight: 600,
                          "&:hover": modSeleccionado ? { textDecoration: "underline" } : {} }}
                      >
                        {estadoSeleccionado}
                      </Typography>
                    </>
                  )}
                  {modSeleccionado && (
                    <>
                      <Typography sx={{ color: "#aaa" }}>›</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 600 }}>
                        {modSeleccionado.nombre}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* NIVEL 1 — Lista de estados */}
                {!estadoSeleccionado && (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                      {["Estado", "Moderadores", "Sin leer", "Total"].map(h => (
                        <Typography key={h} sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                      ))}
                    </Box>
                    {controlData.estados.map((est, idx) => {
                      const nivel = est.sin_leer > 100 ? "#ef4444" : est.sin_leer > 20 ? "#f59e0b" : "#22c55e";
                      return (
                        <Box key={est.estado}
                          onClick={() => setEstadoSel(est.estado)}
                          sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", px: 2, py: 1.5,
                            borderBottom: idx < controlData.estados.length - 1 ? "1px solid #f0f0f0" : "none",
                            cursor: "pointer", "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: nivel }} />
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#333" }}>{est.estado}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>{est.moderadores.length}</Typography>
                          <Typography sx={{ fontSize: "0.85rem", color: est.sin_leer > 0 ? "#ef4444" : "#888", fontWeight: est.sin_leer > 0 ? 700 : 400 }}>
                            {est.sin_leer.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>{est.total_notif.toLocaleString()}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* NIVEL 2 — Moderadores del estado */}
                {estadoSeleccionado && !modSeleccionado && (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                      {["Moderador", "Sin leer", "Total"].map(h => (
                        <Typography key={h} sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                      ))}
                    </Box>
                    {(controlData.estados.find(e => e.estado === estadoSeleccionado)?.moderadores || []).map((mod, idx) => {
                      const mods = controlData.estados.find(e => e.estado === estadoSeleccionado)?.moderadores || [];
                      const nivel = mod.sin_leer > 100 ? "#ef4444" : mod.sin_leer > 20 ? "#f59e0b" : "#22c55e";
                      return (
                        <Box key={mod.uid}
                          onClick={() => { setModSel(mod); fetchNotifMod(mod.uid); }}
                          sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", px: 2, py: 1.5,
                            borderBottom: idx < mods.length - 1 ? "1px solid #f0f0f0" : "none",
                            cursor: "pointer", "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: nivel }} />
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>{mod.nombre}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: "0.85rem", color: mod.sin_leer > 0 ? "#ef4444" : "#888", fontWeight: mod.sin_leer > 0 ? 700 : 400 }}>
                            {mod.sin_leer.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>{mod.total_notif.toLocaleString()}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* NIVEL 3 — Notificaciones del moderador (solo lectura) */}
                {modSeleccionado && (
                  <Box>
                    {/* Header del moderador */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2,
                      backgroundColor: "#fff", borderRadius: 2, p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>
                          {modSeleccionado.nombre}
                        </Typography>
                        <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>
                          {notifMod ? `${notifMod.sin_leer.toLocaleString()} sin leer · ${notifMod.total.toLocaleString()} total` : "Cargando..."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {["all", "unread"].map(f => (
                          <Box key={f} onClick={() => { setFiltroMod(f); fetchNotifMod(modSeleccionado.uid, f === "unread"); }}
                            sx={{ px: 1.5, py: 0.5, borderRadius: 999, cursor: "pointer", fontSize: "0.78rem",
                              backgroundColor: filtroMod === f ? "#ff3333" : "#f0f0f0",
                              color: filtroMod === f ? "#fff" : "#555", transition: "0.15s" }}>
                            {f === "all" ? "Todas" : "Sin leer"}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Lista (solo lectura — sin botones de eliminar) */}
                    <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                      {loadingNotifMod ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                          <CircularProgress sx={{ color: "#ff3333" }} />
                        </Box>
                      ) : !notifMod?.notifications?.length ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                          <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>Sin notificaciones</Typography>
                        </Box>
                      ) : notifMod.notifications.map((n, idx) => {
                        const cfg = TYPE_CONFIG[n.type] || { color: "#888", bg: "#f9f9f9", label: n.type };
                        return (
                          <Box key={n.notification_id}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2, py: 1.8,
                              backgroundColor: n.is_read ? "#fff" : "#fff8f8",
                              "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s" }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: cfg.color, flexShrink: 0, mt: 0.8 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3, flexWrap: "wrap" }}>
                                  <Typography sx={{ fontSize: "0.88rem", fontWeight: n.is_read ? 500 : 700, color: "#222" }}>
                                    {n.titulo}
                                  </Typography>
                                  <Chip label={cfg.label} size="small"
                                    sx={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
                                      fontSize: "0.65rem", height: 18, "& .MuiChip-label": { px: 0.8 } }} />
                                  <Chip
                                    label={n.is_read ? "✓ Vista" : "● No vista"}
                                    size="small"
                                    sx={{ backgroundColor: n.is_read ? "#f0fdf4" : "#fff0f0",
                                      color: n.is_read ? "#22c55e" : "#ef4444",
                                      fontSize: "0.65rem", height: 18, "& .MuiChip-label": { px: 0.8 } }}
                                  />
                                </Box>
                                <Typography sx={{ fontSize: "0.8rem", color: "#666", lineHeight: 1.4 }}>{n.mensaje}</Typography>
                                <Typography sx={{ fontSize: "0.72rem", color: "#aaa", mt: 0.3 }}>{timeAgo(n.created_at)}</Typography>
                              </Box>
                            </Box>
                            {idx < notifMod.notifications.length - 1 && <Divider />}
                          </Box>
                        );
                      })}
                      {/* Paginación simple */}
                      {notifMod?.total > 50 && (
                        <Box sx={{ textAlign: "center", py: 1.5, borderTop: "1px solid #f0f0f0" }}>
                          <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>
                            Mostrando 50 de {notifMod.total.toLocaleString()} — ajusta el filtro para ver menos
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ color: "#aaa" }}>No se pudo cargar el control</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── PASO 3: RESUMEN DOCUMENTOS ── */}
        {isAdmin && catActiva === "documentos" && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
              📄 Documentos por estado — Resumen nacional de validaciones
            </Typography>
            {loadingResumen ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : resumenDocs ? (
              <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.5fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                  {["Estado", "Pendientes", "Validados", "Rechazados", "Última validación"].map(h => (
                    <Typography key={h} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                  ))}
                </Box>
                {resumenDocs.estados.map((row, idx) => (
                  <Box key={row.estado} sx={{
                    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.5fr",
                    px: 2, py: 1.4,
                    borderBottom: idx < resumenDocs.estados.length - 1 ? "1px solid #f0f0f0" : "none",
                    "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s",
                  }}>
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: "#333" }}>{row.estado}</Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.pendientes > 0 ? "#f59e0b" : "#888", fontWeight: row.pendientes > 0 ? 700 : 400 }}>
                      {row.pendientes || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.validados > 0 ? "#22c55e" : "#888", fontWeight: row.validados > 0 ? 600 : 400 }}>
                      {row.validados || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.rechazados > 0 ? "#ef4444" : "#888", fontWeight: row.rechazados > 0 ? 700 : 400 }}>
                      {row.rechazados || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#aaa" }}>
                      {row.ultima_validacion ? timeAgo(row.ultima_validacion) : "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: "#aaa", textAlign: "center", py: 4 }}>No se pudo cargar</Typography>
            )}
          </Box>
        )}

        {/* ── PASO 3: RESUMEN TRAYECTORIAS ── */}
        {isAdmin && catActiva === "trayectorias" && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
              🎯 Trayectorias por estado — Resumen nacional
            </Typography>
            {loadingResumen ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : resumenTray ? (
              <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.5fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                  {["Estado", "Pendientes", "Validadas", "Rechazadas", "Última revisión"].map(h => (
                    <Typography key={h} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                  ))}
                </Box>
                {resumenTray.estados.map((row, idx) => (
                  <Box key={row.estado} sx={{
                    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.5fr",
                    px: 2, py: 1.4,
                    borderBottom: idx < resumenTray.estados.length - 1 ? "1px solid #f0f0f0" : "none",
                    "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s",
                  }}>
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: "#333" }}>{row.estado}</Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.pendientes > 0 ? "#f59e0b" : "#888", fontWeight: row.pendientes > 0 ? 700 : 400 }}>
                      {row.pendientes || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.validadas > 0 ? "#22c55e" : "#888", fontWeight: row.validadas > 0 ? 600 : 400 }}>
                      {row.validadas || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.rechazadas > 0 ? "#ef4444" : "#888", fontWeight: row.rechazadas > 0 ? 700 : 400 }}>
                      {row.rechazadas || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#aaa" }}>
                      {row.ultima_revision ? timeAgo(row.ultima_revision) : "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: "#aaa", textAlign: "center", py: 4 }}>No se pudo cargar</Typography>
            )}
          </Box>
        )}

        {/* ── PASO 3: RESUMEN PROGRESOS ── */}
        {isAdmin && catActiva === "progresos" && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
              📊 Backlog de actividades por estado — Ordenado por mayor pendiente
            </Typography>
            {loadingResumen ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : resumenProg ? (
              <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                  {["Estado", "Sin calificar", "Días máx.", "Acción"].map(h => (
                    <Typography key={h} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                  ))}
                </Box>
                {resumenProg.estados.map((row, idx) => {
                  const critico = row.dias_max_sin_atender > 14;
                  const advertencia = row.dias_max_sin_atender > 7;
                  const color = critico ? "#ef4444" : advertencia ? "#f59e0b" : "#22c55e";
                  return (
                    <Box key={row.estado} sx={{
                      display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr",
                      px: 2, py: 1.4,
                      borderBottom: idx < resumenProg.estados.length - 1 ? "1px solid #f0f0f0" : "none",
                      "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s",
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
                        <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: "#333" }}>{row.estado}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.83rem", color: color, fontWeight: 700 }}>
                        {row.sin_calificar.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: "0.83rem", color: color, fontWeight: advertencia ? 700 : 400 }}>
                        {row.dias_max_sin_atender ? `${row.dias_max_sin_atender} días` : "—"}
                      </Typography>
                      <Box>
                        {advertencia && (
                          <Box
                            onClick={() => enviarAvisoModerador(row.estado)}
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.5,
                              px: 1.2, py: 0.4, borderRadius: 1, cursor: "pointer",
                              backgroundColor: enviandoAviso === row.estado ? "#f0f0f0" : "#fff0f0",
                              border: "1px solid #ef4444", color: "#ef4444",
                              fontSize: "0.72rem", fontWeight: 600,
                              "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s",
                              opacity: enviandoAviso === row.estado ? 0.6 : 1,
                            }}
                          >
                            {enviandoAviso === row.estado ? "Enviando..." : "⚠️ Enviar aviso"}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography sx={{ color: "#aaa", textAlign: "center", py: 4 }}>No se pudo cargar</Typography>
            )}
          </Box>
        )}

        {/* ── PASO 3: RESUMEN ENTREVISTAS ── */}
        {isAdmin && catActiva === "entrevistas" && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
              🤝 Entrevistas por estado — Resumen nacional
            </Typography>
            {loadingResumen ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ff3333" }} />
              </Box>
            ) : resumenEntrev ? (
              <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", px: 2, py: 1.2, backgroundColor: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                  {["Estado", "Pendientes", "Validadas", "Sin solicitar"].map(h => (
                    <Typography key={h} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#666", textTransform: "uppercase" }}>{h}</Typography>
                  ))}
                </Box>
                {resumenEntrev.estados.map((row, idx) => (
                  <Box key={row.estado} sx={{
                    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    px: 2, py: 1.4,
                    borderBottom: idx < resumenEntrev.estados.length - 1 ? "1px solid #f0f0f0" : "none",
                    "&:hover": { backgroundColor: "#fef2f2" }, transition: "0.15s",
                  }}>
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: "#333" }}>{row.estado}</Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.pendientes > 0 ? "#f59e0b" : "#888", fontWeight: row.pendientes > 0 ? 700 : 400 }}>
                      {row.pendientes || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: row.validadas > 0 ? "#22c55e" : "#888", fontWeight: row.validadas > 0 ? 600 : 400 }}>
                      {row.validadas || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", color: "#888" }}>
                      {row.sin_solicitar || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: "#aaa", textAlign: "center", py: 4 }}>No se pudo cargar</Typography>
            )}
          </Box>
        )}

        {/* ── PANEL SOLICITUDES DE FACILITADORES (solo admin) ────────────── */}
        {isAdmin && catActiva === "solicitudes" && tabActiva === "bandeja" && (
          <Box sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden", mb: 2 }}>
            {/* Header */}
            <Box sx={{ backgroundColor: "#7c3aed", px: 2, py: 1.4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaShieldAlt color="#fff" size={14} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>
                  SOLICITUDES DE FACILITADORES
                </Typography>
              </Box>
              <Chip
                label={`${(resumenSolicitudes || []).length} pendientes`}
                size="small"
                sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.72rem", height: 20 }}
              />
            </Box>

            {loadingResumen ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} sx={{ color: "#7c3aed" }} />
              </Box>
            ) : !resumenSolicitudes || resumenSolicitudes.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <FaShieldAlt size={28} color="#d8b4fe" />
                <Typography sx={{ color: "#aaa", fontSize: "0.85rem", mt: 1 }}>
                  No hay solicitudes pendientes
                </Typography>
              </Box>
            ) : (
              resumenSolicitudes.map((sol) => (
                <Box key={sol.solicitud_id} sx={{
                  px: 2, py: 2,
                  borderBottom: "1px solid #f3f0ff",
                  backgroundColor: "#faf5ff",
                  "&:last-child": { borderBottom: "none" },
                }}>
                  {/* Fila superior: nombre + estado */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#3b0764" }}>
                        {sol.nombre} {sol.apellido_pat}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#7c3aed" }}>
                        {sol.matricula} · {sol.estado_republica || "—"}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        sol.tipo === "eliminacion"            ? "Eliminación"
                        : sol.tipo === "inactivacion"         ? "Inactivación"
                        : sol.tipo === "inhabilitar_programa" ? "Inhabilitar programa"
                        : sol.tipo === "activar_programa"     ? "Reactivar programa"
                        : "Baja de programa"
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          sol.tipo === "eliminacion" || sol.tipo === "baja_programa"
                            ? "#fef2f2"
                            : sol.tipo === "activar_programa"
                            ? "#f0fdf4"
                            : "#fffbeb",
                        color:
                          sol.tipo === "eliminacion" || sol.tipo === "baja_programa"
                            ? "#dc2626"
                            : sol.tipo === "activar_programa"
                            ? "#15803d"
                            : "#b45309",
                        fontWeight: 700, fontSize: "0.72rem",
                      }}
                    />
                  </Box>

                  {/* Motivo */}
                  <Box sx={{ backgroundColor: "#ede9fe", borderRadius: 1, px: 1.5, py: 0.8, mb: 1.2 }}>
                    <Typography sx={{ fontSize: "0.78rem", color: "#4c1d95" }}>
                      <strong>Motivo:</strong> {sol.motivo}
                    </Typography>
                    {sol.programa_nombre && (
                      <Typography sx={{ fontSize: "0.73rem", color: "#6d28d9", mt: 0.3 }}>
                        Programa: <strong>{sol.programa_nombre}</strong>
                      </Typography>
                    )}
                    {sol.sol_nombre && (
                      <Typography sx={{ fontSize: "0.73rem", color: "#6d28d9", mt: 0.3 }}>
                        Solicitado por: {sol.sol_nombre} {sol.sol_apellido}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", mt: 0.2 }}>
                      {timeAgo(sol.created_at)}
                    </Typography>
                  </Box>

                  {/* Botones de acción */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Box
                      onClick={() => accionando !== sol.solicitud_id && resolverSolicitud(sol.solicitud_id, "aprobar")}
                      sx={{
                        flex: 1, py: 0.9, borderRadius: 1, cursor: "pointer", textAlign: "center",
                        backgroundColor:
                          sol.tipo === "eliminacion" || sol.tipo === "baja_programa"
                            ? "#fee2e2"
                            : sol.tipo === "activar_programa"
                            ? "#dcfce7"
                            : "#fef3c7",
                        color:
                          sol.tipo === "eliminacion" || sol.tipo === "baja_programa"
                            ? "#dc2626"
                            : sol.tipo === "activar_programa"
                            ? "#15803d"
                            : "#92400e",
                        fontWeight: 700, fontSize: "0.8rem",
                        opacity: accionando === sol.solicitud_id ? 0.6 : 1,
                        "&:hover": { opacity: 0.85 },
                        transition: "0.15s",
                      }}
                    >
                      {accionando === sol.solicitud_id ? "..."
                        : sol.tipo === "eliminacion"          ? "✕ Eliminar"
                        : sol.tipo === "inactivacion"         ? "Inactivar"
                        : sol.tipo === "inhabilitar_programa" ? "⊘ Inhabilitar"
                        : sol.tipo === "activar_programa"     ? "✓ Reactivar"
                        : "🗑 Dar de baja"}
                    </Box>
                    <Box
                      onClick={() => accionando !== sol.solicitud_id && resolverSolicitud(sol.solicitud_id, "rechazar")}
                      sx={{
                        flex: 1, py: 0.9, borderRadius: 1, cursor: "pointer", textAlign: "center",
                        backgroundColor: "#f0fdf4", color: "#15803d",
                        fontWeight: 700, fontSize: "0.8rem",
                        opacity: accionando === sol.solicitud_id ? 0.6 : 1,
                        "&:hover": { opacity: 0.85 },
                        transition: "0.15s",
                      }}
                    >
                      {accionando === sol.solicitud_id ? "..." : "Rechazar solicitud"}
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {/* ── BANDEJA (oculta cuando admin está en modo dashboard, control o resúmenes) ── */}
        {(!isAdmin || (
          tabActiva === "bandeja" &&
          catActiva !== "control" &&
          catActiva !== "documentos" &&
          catActiva !== "trayectorias" &&
          catActiva !== "progresos" &&
          catActiva !== "entrevistas" &&
          catActiva !== "solicitudes"
        )) && (
          <>
            {/* Aviso fijo institucional */}
            {avisoFijo && (
              <Box sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                backgroundColor: "#fffbeb", border: "1px solid #fde68a",
                borderRadius: 2, p: 2, mb: 2,
              }}>
                <FaBullhorn size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#92400e" }}>
                      {avisoFijo.titulo}
                    </Typography>
                    <Chip label="Importante" size="small"
                      sx={{ backgroundColor: "#f59e0b", color: "#fff", fontSize: "0.65rem", height: 18 }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem", color: "#78350f", lineHeight: 1.4 }}>
                    {avisoFijo.mensaje}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#92400e", mt: 0.5 }}>
                    Publicado el {new Date(avisoFijo.created_at).toLocaleDateString("es-MX")}
                  </Typography>
                </Box>
                <Box onClick={() => markRead(avisoFijo.notification_id)}
                  sx={{ cursor: "pointer", color: "#f59e0b", "&:hover": { color: "#d97706" } }}>
                  ✕
                </Box>
              </Box>
            )}

            {/* Tabs Todas / No leídas / Leídas */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              mb: 2, flexWrap: "wrap",
            }}>
              {[
                { key: "all",    label: `Todas (${items.length})` },
                { key: "unread", label: `No leídas (${unreadCount})` },
                { key: "read",   label: `Leídas (${items.length - unreadCount})` },
              ].map(tab => (
                <Box
                  key={tab.key}
                  onClick={() => setFiltro(tab.key)}
                  sx={{
                    px: 2, py: 0.7, borderRadius: 999, cursor: "pointer",
                    backgroundColor: filtro === tab.key ? "#ff3333" : "#f0f0f0",
                    color: filtro === tab.key ? "#fff" : "#555",
                    fontSize: "0.82rem", fontWeight: filtro === tab.key ? 700 : 400,
                    transition: "0.15s",
                    "&:hover": { backgroundColor: filtro === tab.key ? "#e02a2a" : "#e5e5e5" },
                  }}
                >
                  {tab.label}
                </Box>
              ))}

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Marcar todas como leídas */}
              <Box
                onClick={markAllRead}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.8,
                  cursor: "pointer", color: "#ff3333", fontSize: "0.8rem", fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <FaCheckDouble size={12} />
                Marcar todas como leídas
              </Box>

              {/* Eliminar todas */}
              <Box
                onClick={deleteAll}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.8,
                  cursor: "pointer", color: "#ef4444", fontSize: "0.8rem", fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <FaTrash size={11} />
                Eliminar todas
              </Box>
            </Box>

            {/* Barra de selección masiva */}
            {itemsFiltrados.length > 0 && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1,
                mb: 1.5, px: 1,
              }}>
                <Checkbox
                  size="small"
                  checked={selected.length === itemsFiltrados.length && itemsFiltrados.length > 0}
                  indeterminate={selected.length > 0 && selected.length < itemsFiltrados.length}
                  onChange={toggleSelectAll}
                  sx={{ color: "#ff3333", "&.Mui-checked": { color: "#ff3333" }, p: 0.5 }}
                />
                <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>
                  {selected.length > 0
                    ? `${selected.length} seleccionada(s)`
                    : "Seleccionar todas"}
                </Typography>
              </Box>
            )}

            {/* Lista */}
            <Box sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress sx={{ color: "#ff3333" }} />
                </Box>
              ) : itemsFiltrados.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <FaBell size={40} color="#ddd" />
                  <Typography sx={{ color: "#bbb", mt: 1.5, fontSize: "0.9rem" }}>
                    No hay notificaciones en esta categoría
                  </Typography>
                </Box>
              ) : (
                itemsFiltrados.map((n, idx) => {
                  const cfg = TYPE_CONFIG[n.type] || { color: "#888", bg: "#f9f9f9", label: n.type };
                  const link = QUICK_LINKS[n.type];
                  const isSel = selected.includes(n.notification_id);

                  return (
                    <Box key={n.notification_id}>
                      <Box
                        sx={{
                          display: "flex", alignItems: "flex-start", gap: 1.5,
                          px: 2, py: 1.8,
                          backgroundColor: isSel
                            ? "#fff0f0"
                            : n.is_read ? "#fff" : "#fff8f8",
                          transition: "0.15s",
                          "&:hover": { backgroundColor: "#fef2f2" },
                        }}
                      >
                        {/* Checkbox */}
                        <Checkbox
                          size="small"
                          checked={isSel}
                          onChange={() => toggleSelect(n.notification_id)}
                          sx={{ color: "#ff3333", "&.Mui-checked": { color: "#ff3333" }, p: 0.3, mt: 0.3 }}
                        />

                        {/* Dot de color */}
                        <Box sx={{
                          width: 10, height: 10, borderRadius: "50%",
                          backgroundColor: cfg.color, flexShrink: 0, mt: 0.8,
                        }} />

                        {/* Contenido */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.3 }}>
                            <Typography sx={{
                              fontSize: "0.88rem",
                              fontWeight: n.is_read ? 500 : 700,
                              color: "#222",
                            }}>
                              {n.titulo}
                            </Typography>
                            <Chip
                              label={cfg.label}
                              size="small"
                              sx={{
                                backgroundColor: cfg.bg,
                                color: cfg.color,
                                border: `1px solid ${cfg.color}`,
                                fontSize: "0.65rem", height: 18,
                                "& .MuiChip-label": { px: 0.8 },
                              }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: "0.8rem", color: "#666", lineHeight: 1.4, mb: 0.5 }}>
                            {n.mensaje}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: "0.72rem", color: "#aaa" }}>
                              {timeAgo(n.created_at)}
                            </Typography>
                            <Chip
                              label={n.is_read ? "Leída" : "No leída"}
                              size="small"
                              sx={{
                                backgroundColor: n.is_read ? "#f0f0f0" : "#fff0f0",
                                color: n.is_read ? "#888" : "#ff3333",
                                fontSize: "0.65rem", height: 17,
                                "& .MuiChip-label": { px: 0.8 },
                              }}
                            />
                            {link && (
                            <Typography
                              onClick={() => {
                                if (n.type === "uploads_pendientes" || n.type === "docs_pendientes") {
                                  const matriculaMatch = n.mensaje?.match(/\(([A-Z]+-\d+)\)/);
                                  const programaMatch = n.mensaje?.match(/en ['"]([^'"]+)['"]/);
                                  const params = new URLSearchParams();
                                  if (matriculaMatch?.[1]) params.set("matricula", matriculaMatch[1]);
                                  if (programaMatch?.[1]) params.set("programa", programaMatch[1]);
                                  // tab=1 → Actividades Upload, tab=4 → Documentos
                                  params.set("tab", n.type === "uploads_pendientes" ? "1" : "4");
                                  navigate(`${link.path}?${params.toString()}`);
                                } else if (n.type === "doc_nuevo" || n.type === "trayectoria_nueva") {
                                  // Extraer matricula del mensaje o metadata para ir directo al usuario
                                  const meta = typeof n.metadata === "string" ? JSON.parse(n.metadata || "{}") : (n.metadata || {});
                                  const matricula = meta.matricula || n.mensaje?.match(/\(([A-Z]+-\d+)\)/)?.[1];
                                  if (matricula) {
                                    navigate(`${link.path}?search=${encodeURIComponent(matricula)}&field=matricula`);
                                  } else {
                                    navigate(link.path);
                                  }
                                } else {
                                  navigate(link.path);
                                }
                              }}
                              >
                                {link.label} →
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Acciones */}
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                          {!n.is_read && (
                            <Tooltip title="Marcar como leída">
                              <IconButton
                                size="small"
                                onClick={() => markRead(n.notification_id)}
                                sx={{ color: "#22c55e", "&:hover": { backgroundColor: "#f0fdf4" } }}
                              >
                                <FaCheck size={12} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                await apiFetch(`/api/notifications/${n.notification_id}`, { method: "DELETE" });
                                setItems(prev => prev.filter(x => x.notification_id !== n.notification_id));
                                if (!n.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
                              }}
                              sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#fef2f2" } }}
                            >
                              <FaTrash size={12} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      {idx < itemsFiltrados.length - 1 && <Divider />}
                    </Box>
                  );
                })
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ══════════════════════════════════════════════════
          COLUMNA DERECHA — resumen + enlaces rápidos
      ══════════════════════════════════════════════════ */}
      <Box sx={{ width: 220, flexShrink: 0 }}>

        {/* Resumen */}
        <Box sx={{
          backgroundColor: "#fff", borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden", mb: 2,
        }}>
          <Box sx={{ backgroundColor: "#ff3333", px: 2, py: 1.2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 1 }}>
              RESUMEN
            </Typography>
          </Box>
          <Box sx={{ p: 2, display: "flex", gap: 1 }}>
            {[
              { label: "No leídas", value: unreadCount,                      color: "#ff3333", bg: "#fff0f0" },
              { label: "Leídas",    value: items.length - unreadCount,        color: "#22c55e", bg: "#f0fdf4" },
              { label: "Total",     value: items.length,                      color: "#3b82f6", bg: "#eff6ff" },
            ].map(s => (
              <Box key={s.label} sx={{
                flex: 1, textAlign: "center", py: 1.2, borderRadius: 1.5,
                backgroundColor: s.bg,
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: s.color, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: "0.65rem", color: "#888", mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Enlaces rápidos */}
        <Box sx={{
          backgroundColor: "#fff", borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "hidden", mb: 2,
        }}>
          <Box sx={{ backgroundColor: "#3b82f6", px: 2, py: 1.2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 1 }}>
              ENLACES RÁPIDOS
            </Typography>
          </Box>
          <Box sx={{ p: 1 }}>
            {(isStaff ? [
              { label: "Ver documentos pendientes", path: "/Documentos", color: "#22c55e" },
              { label: "Ver trayectorias",          path: "/Trayectorias", color: "#ef4444" },
              { label: "Ver solicitudes entrevista",path: "/Usuarios",    color: "#3b82f6" },
              { label: "Ver progresos",             path: "/Progresos",   color: "#f59e0b" },
              { label: "Ver solicitudes facilitadores", path: "/Usuarios", color: "#7c3aed" },
            ] : [
              { label: "Ver mis documentos",  path: "/MiPerfil",    color: "#22c55e" },
              { label: "Ver mi trayectoria",  path: "/Trayectoria", color: "#ef4444" },
              { label: "Ir a mis programas",  path: "/Plataforma",  color: "#3b82f6" },
            ]).map((l, i) => (
              <Box
                key={i}
                onClick={() => navigate(l.path)}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 1.5, py: 1, cursor: "pointer", borderRadius: 1,
                  "&:hover": { backgroundColor: "#f5f5f5" }, transition: "0.15s",
                }}
              >
                <Typography sx={{ fontSize: "0.78rem", color: "#444" }}>{l.label}</Typography>
                <Typography sx={{ color: l.color, fontSize: "0.8rem" }}>›</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Info retención */}
        <Box sx={{
          backgroundColor: "#eff6ff", borderRadius: 2,
          border: "1px solid #bfdbfe", p: 1.8,
        }}>
          <Typography sx={{ fontSize: "0.72rem", color: "#1e40af", lineHeight: 1.5 }}>
            ℹ️ Las notificaciones se mantienen hasta que las elimines. Puedes conservarlas para consultarlas en cualquier momento.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}